import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';

export default function CustomersPage() {
  const currencyCode = useCurrencyCode();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/customers', {
        params: { search: search || undefined },
      });
      setCustomers(res.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to load customers.');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const viewCustomer = async (customerId) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/customers/${customerId}`);
      setSelectedCustomer(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to load customer.');
    } finally {
      setLoadingDetail(false);
    }
  };

  const stats = useMemo(() => {
    const total = customers.length;
    const marketing = customers.filter((c) => c.marketingOptIn).length;
    const whatsapp = customers.filter((c) => c.whatsappOptIn).length;
    const sms = customers.filter((c) => c.smsOptIn).length;
    return { total, marketing, whatsapp, sms };
  }, [customers]);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-hidden bg-slate-950 px-6 py-6 text-white">
      <header className="flex flex-col gap-2">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Customers</p>
        <h1 className="text-3xl font-semibold">Directory</h1>
        <p className="text-sm text-slate-400">
          Browse saved customers, opt-in flags, and recent receipts pulled from `/api/customers`.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total profiles" value={stats.total} />
        <StatCard label="Marketing opt-in" value={stats.marketing} tone="emerald" />
        <StatCard label="WhatsApp opt-in" value={stats.whatsapp} tone="emerald" />
        <StatCard label="SMS opt-in" value={stats.sms} tone="emerald" />
      </div>

      <div className="grid flex-1 gap-6 lg:grid-cols-[1.1fr,1.2fr]">
        <section className="flex flex-col rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <form onSubmit={handleSearchSubmit} className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Search</label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Name, email, or phone"
                className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              />
            </div>
            <button
              type="submit"
              className="rounded-2xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400"
            >
              Apply
            </button>
          </form>

          <div className="mt-4 flex-1 overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/50">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 text-xs text-slate-400">
              <span>{customers.length} result{customers.length === 1 ? '' : 's'}</span>
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                }}
                className="rounded-full border border-slate-700 px-3 py-1 text-[11px] uppercase tracking-wide text-slate-300 hover:border-slate-500"
              >
                Clear
              </button>
            </div>
            <ul className="h-full divide-y divide-slate-800 overflow-y-auto">
              {loading && (
                <li className="px-4 py-4 text-center text-xs text-slate-400">Loading customers…</li>
              )}
              {!loading && customers.length === 0 && (
                <li className="px-4 py-4 text-center text-xs text-slate-400">No customers found.</li>
              )}
              {customers.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    onClick={() => viewCustomer(customer.id)}
                    className={`flex w-full flex-col gap-1 px-4 py-3 text-left text-sm transition hover:bg-slate-900 ${
                      selectedCustomer?.id === customer.id ? 'bg-emerald-500/20 text-emerald-100' : 'text-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{customer.name || 'Unnamed customer'}</span>
                      <span className="text-xs text-slate-400">{customer.email || customer.phone || '—'}</span>
                    </div>
                    <div className="text-xs text-slate-400">
                      {[
                        customer.marketingOptIn && 'Email opt-in',
                        customer.whatsappOptIn && 'WhatsApp opt-in',
                        customer.smsOptIn && 'SMS opt-in',
                      ]
                        .filter(Boolean)
                        .join(' • ') || 'No opt-ins'}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          {loadingDetail && (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">Loading customer…</div>
          )}
          {!loadingDetail && selectedCustomer ? (
            <div className="flex h-full flex-col gap-4 overflow-auto">
              <header>
                <h2 className="text-2xl font-semibold text-white">{selectedCustomer.name || 'Unnamed customer'}</h2>
                <p className="text-sm text-slate-400">{selectedCustomer.email || 'No email provided'}</p>
                <p className="text-sm text-slate-400">{selectedCustomer.phone || 'No phone provided'}</p>
              </header>

              <div className="space-y-2 rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-sm text-slate-200">
                <p>
                  <span className="text-slate-500">Marketing opt-in:</span>{' '}
                  <strong>{selectedCustomer.marketingOptIn ? 'Yes' : 'No'}</strong>
                </p>
                <p>
                  <span className="text-slate-500">WhatsApp opt-in:</span>{' '}
                  <strong>{selectedCustomer.whatsappOptIn ? 'Yes' : 'No'}</strong>
                </p>
                <p>
                  <span className="text-slate-500">SMS opt-in:</span>{' '}
                  <strong>{selectedCustomer.smsOptIn ? 'Yes' : 'No'}</strong>
                </p>
                <p className="text-xs text-slate-400">
                  Last receipt: {selectedCustomer.lastReceiptAt ? new Date(selectedCustomer.lastReceiptAt).toLocaleString() : '—'}
                </p>
                <p className="text-xs text-slate-400">
                  Loyalty invite: {selectedCustomer.loyaltyInvitedAt ? new Date(selectedCustomer.loyaltyInvitedAt).toLocaleString() : 'Not yet invited'}
                </p>
                {selectedCustomer.notes && (
                  <p className="rounded-2xl border border-slate-800 bg-slate-950/70 p-3 text-xs text-slate-300">
                    {selectedCustomer.notes}
                  </p>
                )}
              </div>

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent receipts</h3>
                {selectedCustomer.receipts && selectedCustomer.receipts.length > 0 ? (
                  <ul className="mt-3 space-y-2 rounded-2xl border border-slate-800 bg-slate-950/50 p-3 text-sm text-slate-200">
                    {selectedCustomer.receipts.map((receipt) => (
                      <li key={receipt.id} className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">#{receipt.id}</p>
                          <p className="text-xs text-slate-400">{new Date(receipt.createdAt).toLocaleString()}</p>
                        </div>
                        <span>{formatCurrency(receipt.total, currencyCode)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-500">
                    No recent receipts found.
                  </p>
                )}
              </div>
            </div>
          ) : (
            !loadingDetail && (
              <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 text-sm text-slate-500">
                Select a customer to view details.
              </div>
            )
          )}
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = 'slate' }) {
  const palette = {
    slate: 'border-slate-800 bg-slate-950/50 text-slate-100',
    emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100',
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 ${palette[tone] || palette.slate}`}>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
}
